import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { deleteCategory, getCategories, moveCategory, setCategoryVisibility, upsertCategory } from '../services/categoryService';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    try {
      setCategories(getCategories({ includeHidden: true }));
    } catch (e) {
      toast({ title: 'Failed to load categories', description: String(e?.message || 'Please try again.') });
    }
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('ditris:categories', handler);
    return () => window.removeEventListener('ditris:categories', handler);
  }, []);

  const canDelete = useMemo(() => (c) => c?.slug && c.slug !== 'new-arrivals', []);

  const onCreateOrUpdate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: 'Missing name', description: 'Enter a category name.' });
      return;
    }

    setSaving(true);
    try {
      const created = await upsertCategory({ name: trimmed, slug: slug.trim(), visible: true });
      toast({ title: 'Saved', description: `${created.name} is ready.` });
      setName('');
      setSlug('');
      refresh();
    } catch (e) {
      toast({ title: 'Save failed', description: String(e?.message || 'Please try again.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-wide">CATEGORIES</h1>
          <p className="mt-2 text-sm text-gray-600">Manage navigation categories used across the storefront and admin.</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="text-sm font-medium tracking-wide">CREATE / UPDATE</div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-800">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. SHIRTS"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800">Slug (optional)</label>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="e.g. shirts"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black transition-colors"
                />
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={onCreateOrUpdate}
                className="inline-flex items-center justify-center gap-2 w-full rounded-lg bg-black text-white px-5 py-3 text-sm font-medium hover:bg-gray-900 transition-colors disabled:opacity-60"
              >
                <Plus size={16} />
                Save Category
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="text-xs tracking-widest text-gray-600">CATEGORY LIST</div>
            </div>
            <div className="divide-y divide-gray-200">
              {categories.length === 0 ? (
                <div className="px-6 py-10 text-sm text-gray-600">No categories yet.</div>
              ) : (
                categories.map((c, idx) => (
                  <div key={c.slug} className="px-6 py-4 flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium text-black">{c.name}</div>
                      <div className="mt-1 text-xs text-gray-600">/{c.slug}</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveCategory(c.slug, 'up')}
                        className={`inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors ${
                          idx === 0 ? 'opacity-40 cursor-not-allowed' : ''
                        }`}
                        aria-label="Move up"
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        type="button"
                        disabled={idx === categories.length - 1}
                        onClick={() => moveCategory(c.slug, 'down')}
                        className={`inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors ${
                          idx === categories.length - 1 ? 'opacity-40 cursor-not-allowed' : ''
                        }`}
                        aria-label="Move down"
                      >
                        <ArrowDown size={16} />
                      </button>

                      <button
                        type="button"
                        onClick={() => setCategoryVisibility(c.slug, c.visible === false)}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-50 transition-colors"
                        aria-label={c.visible === false ? 'Show category' : 'Hide category'}
                      >
                        {c.visible === false ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>

                      <button
                        type="button"
                        disabled={!canDelete(c)}
                        onClick={async () => {
                          if (!canDelete(c)) return;
                          const ok = window.confirm(`Delete category “${c.name}”?`);
                          if (!ok) return;
                          await deleteCategory(c.slug);
                        }}
                        className={`inline-flex items-center justify-center rounded-lg border border-red-200 bg-white p-2 text-red-700 hover:bg-red-50 transition-colors ${
                          !canDelete(c) ? 'opacity-40 cursor-not-allowed' : ''
                        }`}
                        aria-label="Delete category"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
